import { faMinus as lightMinus } from "@jewlr/pro-light-svg-icons/faMinus"
import { faPlus as lightPlus } from "@jewlr/pro-light-svg-icons/faPlus"
import { createSelector } from "@reduxjs/toolkit"
import PropTypes from "prop-types"
import { memo, useCallback, useLayoutEffect, useMemo, useRef } from "react"
import { shallowEqual, useSelector, useDispatch } from "react-redux"
import scroll from "scroll"
import scrollDoc from "scroll-doc"

import { makeShouldUseNewWizardExp } from "areas/product/jewlr"
import {
  clearDefault,
  scrollPanelIndex,
  togglePanelIndex,
} from "areas/product/store/actions"
import Button from "components/ui/button"
import FontAwesomeIcon from "components/ui/font-awesome-icon"
import displayOptionDescription from "helpers/product/display-option-description"
import displayOptionHeading from "helpers/product/display-option-heading"
import displayOptionPrice from "helpers/product/display-option-price"
import { hasHebrewChar } from "helpers/product/text-dir"
import { cn } from "styles/utils"

import AccordionThumbnail from "./accordion-thumbnail"

const selectChoicesMap = (state) => state.choices
const selectDiamondSelector = (state) => state.diamondSelector
const selectDiamondUI = (state) => state.diamondUI
const selectMiniviewScrollHide = (state) =>
  state.previewImageUI.miniview.scrollHide
const selectOpenedPanelIndexes = (state) => state.panel.openedPanelIndexes
const selectPrefStore = (state) => state.wizardUI.prefStore
const selectScrollPanelIndex = (state) => state.panel.scrollPanelIndex

const makeSelectAccordionHeadingData = (pref, shouldUseNewWizardExp) => {
  const selectWizardData = (state) => state.wizard.data[pref]

  return createSelector(
    [
      selectWizardData,
      selectChoicesMap,
      selectDiamondSelector,
      selectDiamondUI,
      selectMiniviewScrollHide,
      selectOpenedPanelIndexes,
      selectPrefStore,
      selectScrollPanelIndex,
    ],
    (
      wizardData,
      choices,
      diamondSelector,
      diamondUI,
      hideMiniview,
      openedPanelIndexes,
      prefStore,
      scrollPanel
    ) => {
      return {
        description: displayOptionDescription(
          wizardData,
          pref,
          choices,
          diamondSelector,
          diamondUI
        ),
        hideMiniview,
        isOpened: openedPanelIndexes.includes(pref),
        prefStore,
        price: displayOptionPrice(
          {
            choices,
            diamondSelector,
            diamondUI,
            wizard: { data: { [pref]: wizardData } },
          },
          pref
        ),
        scrollPanel,
        wizardData,
        wizardHeadingTitle: displayOptionHeading(
          wizardData,
          pref,
          choices,
          shouldUseNewWizardExp
        ),
      }
    }
  )
}

const AccordionHeading = ({ pref }) => {
  const headingRef = useRef()
  const dispatch = useDispatch()

  const shouldUseNewWizardExp = makeShouldUseNewWizardExp()
  const selectAccordionHeadingData = useMemo(
    () => makeSelectAccordionHeadingData(pref, shouldUseNewWizardExp),
    [pref, shouldUseNewWizardExp]
  )

  const {
    description,
    hideMiniview,
    hidePrice,
    isOpened,
    price,
    scrollPanel,
    wizardData,
    wizardHeadingTitle,
  } = useSelector(selectAccordionHeadingData, shallowEqual)

  const wizardDataHeading = wizardData && wizardData.heading

  const togglePanel = useCallback(() => {
    const panelPref = wizardDataHeading?.pref

    if (
      panelPref === "ef" ||
      panelPref === "ef2" ||
      /img[0-9]+/.test(panelPref)
    ) {
      dispatch(clearDefault())
    }
    dispatch(togglePanelIndex(panelPref, pref, shouldUseNewWizardExp))
  }, [dispatch, pref, wizardDataHeading, shouldUseNewWizardExp])

  useLayoutEffect(() => {
    if (scrollPanel === pref) {
      const timeout = setTimeout(() => {
        const page = scrollDoc()
        let pageOffset = hideMiniview ? 80 : 200
        const openedHeading = headingRef.current
        if (openedHeading) {
          const yOffset = openedHeading.offsetTop - pageOffset
          scroll.top(page, yOffset)
          dispatch(scrollPanelIndex(""))
        }
      }, 300)
      return () => clearTimeout(timeout)
    }
  }, [scrollPanel, pref, hideMiniview, dispatch])

  if (
    !wizardDataHeading ||
    wizardDataHeading.disabled ||
    wizardData.disabled ||
    (/^s[0-9]+/.test(pref) &&
      wizardData.options.length <= 1 &&
      !wizardData.override_text)
  ) {
    return null
  }

  return (
    <>
      {/^face[0-9]+$/.test(wizardDataHeading.pref) && (
        // added face option header
        <div>
          <h2 className="tablet:block text-20 border-b-grey-35 tablet:px-0 tablet:pb-0 tablet:my-16 hidden border-b-2 px-10 pb-5 font-bold uppercase tracking-wider">
            {"SIDE " + wizardDataHeading.pref.replace(/\D/g, "")}
          </h2>

          <div className="py-25 bg-grey-4 border-t-grey-10 tablet:hidden border-t px-20">
            <div className="text-14 border-b-grey-20 tracking-half leading-0 m-0 w-full border-b text-center font-bold uppercase text-black">
              <span className="bg-grey-4 px-10">
                {"SIDE " + wizardDataHeading.pref.replace(/\D/g, "")}
              </span>
            </div>
          </div>
        </div>
      )}
      <div
        className={cn(
          shouldUseNewWizardExp ? "py-4" : "border-b-grey-10 border-b p-4",
          isOpened && !shouldUseNewWizardExp ? "bg-polar" : "bg-white"
        )}
        ref={headingRef}
      >
        <h2>
          <Button
            aria-controls={`wizard-accordion-content-${wizardDataHeading.pref}`}
            aria-expanded={isOpened}
            className={cn(
              "flex w-full justify-between text-left",
              shouldUseNewWizardExp
                ? "leading-18 py-12 outline-offset-0"
                : "tablet:pr-6 py-6 pl-6 pr-14"
            )}
            data-cy={`product-wizard-heading-${wizardDataHeading.pref}`}
            id={`wizard-accordion-heading-${wizardDataHeading.pref}`}
            onClick={togglePanel}
          >
            {shouldUseNewWizardExp ? (
              <p className="text-14 font-medium">
                <span className="font-semibold">{wizardHeadingTitle}: </span>
                {description}
              </p>
            ) : (
              <div className="flex">
                <AccordionThumbnail pref={pref} />
                <div
                  className={cn(
                    "flex flex-col items-start",
                    wizardDataHeading.pref === "diamond" && "justify-center"
                  )}
                >
                  <p className="text-14 tablet:font-bold tablet:tracking-half font-semibold uppercase text-black">
                    {wizardHeadingTitle}
                  </p>
                  <p
                    className={cn(
                      "text-grey-35 text-12 tablet:text-grey-65 tablet:font-normal tablet:tracking-half inline-block font-semibold",
                      wizardDataHeading.ring_size_tips ? "w-full" : "w-auto"
                    )}
                    data-cy={`product-wizard-heading-details-${wizardDataHeading.pref}`}
                    {...(hasHebrewChar(description) ? { dir: "auto" } : {})}
                  >
                    {description}
                  </p>
                </div>
              </div>
            )}

            <div className="flex items-center">
              {!hidePrice && (
                <p
                  className="text-14 tracking-half mr-20 font-bold"
                  {...(wizardDataHeading.pref == "sku"
                    ? { "data-ge-parentproduct-base-price": true }
                    : { "data-ge-subproducts-subsequent-price": true })}
                >
                  {price}
                </p>
              )}
              <FontAwesomeIcon
                className="text-grey-65 text-16"
                icon={isOpened ? lightMinus : lightPlus}
              />
            </div>
          </Button>
        </h2>
      </div>
    </>
  )
}

AccordionHeading.propTypes = {
  pref: PropTypes.string,
}

export default memo(AccordionHeading)
